import { GMetrixDatePicker } from 'components/GMetrixDatePicker/GMetrixDatePicker';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { useExtendOrder } from 'pages/orders/extend_order/context';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatDay } from 'utils';

type DetailItemProps = {
    label: string;
    value: string | number | null | undefined;
};

const DetailItem = (props: DetailItemProps) => {

    const { label, value } = props;

    return (
        <Col className="mb-3" md={4}>
            <dt className="text-muted small mb-1">
                {label}
            </dt>
            <dd className="mb-0">
                {value ?? '—'}
            </dd>
        </Col>
    );

};

export const ExtendStep = props => {

    const { error, extendedBy, handleBack, handleChangeExpire, handleChangeExtendedBy, handleChangeRequestedBy, handleChangeStart,
        handleNext, newExpireDate, newStartDate, requestedBy, selectedOrder } = useExtendOrder();
    const intl = useIntl();
    const { formatMessage } = intl;

    const isNextDisabled = !extendedBy.trim() || !requestedBy.trim() || !newStartDate || !newExpireDate;

    return (
        <div className={'extend-step d-flex flex-column gap-3'}>
            <h6>
                <FormattedMessage defaultMessage="Enter the order extension details below" id="enter_the_order_extension_details_below.6fc989b3" />
            </h6>

            <Card>
                <Card.Body className={'d-flex flex-column gap-3'}>
                    <dl className="mb-0">
                        <Row>
                            <DetailItem
                                label={formatMessage({ id: 'partner_name.4cf8979c', defaultMessage: 'Partner Name' })}
                                value={selectedOrder.Partner?.Name}
                            />
                            <DetailItem
                                label={formatMessage({ id: 'ordered_by.381902ec', defaultMessage: 'Ordered By', description: 'Formulario' })}
                                value={selectedOrder.OrderedBy}
                            />
                            <DetailItem
                                label={formatMessage({ id: 'order_id.ab576b3d', defaultMessage: 'Order ID', description: 'Formulario' })}
                                value={selectedOrder.OrderControl}
                            />
                            <DetailItem
                                label={formatMessage({ id: 'license_name.8773f362', defaultMessage: 'License Name' })}
                                value={selectedOrder.License?.Name}
                            />
                            <DetailItem
                                label={formatMessage({ id: 'date_created.85efe6f3', defaultMessage: 'Date Created' })}
                                value={formatDay(selectedOrder.DateCreate, intl)}
                            />
                            <DetailItem
                                label={formatMessage({ id: 'start_date.66ba5b72', defaultMessage: 'Start Date' })}
                                value={formatDay(selectedOrder.DateStart, intl)}
                            />
                            <DetailItem
                                label={formatMessage({ id: 'expire_date.d6e8a041', defaultMessage: 'Expire Date' })}
                                value={formatDay(selectedOrder.DateExpire, intl)}
                            />
                            <DetailItem
                                label={formatMessage({ id: 'requested.f99eee8e', defaultMessage: 'Requested', description: 'Gridview header' })}
                                value={selectedOrder.NumRequested}
                            />
                            <DetailItem
                                label={formatMessage({ id: 'assigned.f0b32a15', defaultMessage: 'Assigned' })}
                                value={selectedOrder.NumGranted}
                            />
                            <DetailItem
                                label={formatMessage({ id: 'available.d321561a', defaultMessage: 'Available' })}
                                value={selectedOrder.NumAvailable}
                            />
                            <DetailItem
                                label={formatMessage({ id: 'order_status.6667c7c5', defaultMessage: 'Order Status' })}
                                value={selectedOrder.OrderStatusText}
                            />
                            <DetailItem
                                label={formatMessage({ id: 'codes_created.b11b4efe', defaultMessage: 'Codes Created' })}
                                value={selectedOrder.CodeCount}
                            />
                            <DetailItem
                                label={formatMessage({ id: 'codes_redeemed.ef3784be', defaultMessage: 'Codes Redeemed' })}
                                value={selectedOrder.CodesRedeemed}
                            />
                            <DetailItem
                                label={formatMessage({ id: 'renewal_id.c1f265e6', defaultMessage: 'Renewal ID' })}
                                value={selectedOrder.RenewId !== -1 ? selectedOrder.RenewId : 'N/A'}
                            />

                            {selectedOrder.PurchaseOrder && (
                                <DetailItem
                                    label={formatMessage({ id: 'purchase_order.17844493', defaultMessage: 'Purchase Order' })}
                                    value={selectedOrder.PurchaseOrder}
                                />
                            )}

                        </Row>
                        {selectedOrder.Comment && (
                            <Row>
                                <Col className="mb-3" md={12}>
                                    <dt className="text-muted small mb-1">
                                        <FormattedMessage defaultMessage="Comment" description={formatMessage({ id: 'formulario.6dc35e9d', defaultMessage: 'Formulario' })} id="comment.e2d25fe4" />
                                    </dt>
                                    <dd className="mb-0">
                                        {selectedOrder.Comment}
                                    </dd>
                                </Col>
                            </Row>
                        )}
                    </dl>

                    <hr />

                    <Row className="mt-3">
                        <Col md={3}>
                            <GMetrixInput
                                label={formatMessage({ id: 'extended_by.3a058bcc', defaultMessage: 'Extended by' })}
                                onChange={e => handleChangeExtendedBy(e.target.value)}
                                value={extendedBy}
                            />
                        </Col>
                        <Col md={3}>
                            <GMetrixInput
                                label={formatMessage({ id: 'per_request_of.a1b165a2', defaultMessage: 'Per request of' })}
                                onChange={e => handleChangeRequestedBy(e.target.value)}
                                value={requestedBy}
                            />
                        </Col>
                        <Col md={3}>
                            <GMetrixDatePicker
                                isValid
                                label={formatMessage({ id: 'to_start_on.a326d816', defaultMessage: 'To start on' })}
                                onChange={(date: any) => handleChangeStart(date ? new Date(date) : null)}
                                readOnly={selectedOrder.CodeCount > 0}
                                value={newStartDate ?? undefined}
                            />
                        </Col>
                        <Col md={3}>
                            <GMetrixDatePicker
                                isValid
                                label={formatMessage({ id: 'to_expire_on.b9c86e4a', defaultMessage: 'To expire on' })}
                                onChange={(date: any) => handleChangeExpire(date ? new Date(date) : null)}
                                value={newExpireDate ?? undefined}
                            />
                        </Col>
                    </Row>

                    {error && (
                        <div className="text-danger mt-2">
                            {error}
                        </div>
                    )}

                    <div className="d-flex justify-content-end gap-2">
                        <Button onClick={handleBack} variant="secondary">
                            <FormattedMessage defaultMessage="Back" id="back.b6238c9e" />
                        </Button>
                        <Button disabled={isNextDisabled} onClick={handleNext} variant="primary">
                            <FormattedMessage defaultMessage="Next" id="next.50b7834e" />
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );

};
